import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import Ghost from '@gravity-ui/icons/Ghost';
import Shield from '@gravity-ui/icons/Shield';
import i18n from '@yandex-int/i18n';
import cls from 'classnames';
import { getOperationsTranslated } from 'client/api';
import { IconSmall } from 'client/blocks/AppIcons/AppIcons';
import { DescribeAppInIcons } from 'client/blocks/DescribeAppInIcons/DescribeAppInIcons';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { getMapChannelToAppCard } from 'client/blocks/icons/mapChannelToAppCard';
import { SettingPageLayout } from 'client/blocks/Layout/Layout';
import { SideMenu } from 'client/blocks/Menu/Menu';
import { IMenuMobileItem, MenuMobile } from 'client/blocks/MenuMobile/MenuMobile';
import { NdaLink } from 'client/blocks/NdaLink/NdaLink';
import { InstallableAppRevision } from 'client/blocks/Settings/InstallableAppRevision';
import { InstallableAppSettingsPage } from 'client/blocks/SettingsPage/InstallableAppSettingsPage';
import { SharingPage } from 'client/blocks/Sharing/SharingPage';
import { DeploymentStatus, DeploymentStatusMobile } from 'client/blocks/statuses/DeploymentStatus/DeploymentStatus';
import { OperationStatus } from 'client/blocks/statuses/OperationStatus/OperationStatus';
import { RevisionStatus } from 'client/blocks/statuses/RevisionStatus/RevisionStatus';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { SettingsPageContext } from 'client/context/settingsPageContext';
import { useInterval } from 'client/hooks/useInterval';
import { useIsDraft } from 'client/hooks/useIsDraft';
import { moment } from 'client/lib/moment';
import { routes } from 'client/lib/routes';
import { isInDevelopment } from 'client/model/draft';
import { Operation } from 'client/model/operation';
import { InstallableApp } from 'client/model/skill';
import { getIsAdmin, getIsGlobalViewer, getIsYangoRequest, makeSafe } from 'client/utils';
import { getCanViewAsAdmin } from 'client/utils';
import { getSquareLogoUrl } from 'client/utils/settings/installable-app';
import { getRevisionSlug } from 'client/utils/time';
import * as accessKeyset from 'i18n/Access';
import * as keyset from 'i18n/AppsIndex';
import * as footerKeyset from 'i18n/Footer';

import styles from './AppPage.module.scss';
import { getAppHeader } from './utils';

interface Props {
    installableApp: InstallableApp;
    setInstallableApp: (app: InstallableApp) => void;
    refreshInstallableApp: () => void;
}

const tAccess = i18n(accessKeyset);
const tFooter = i18n(footerKeyset);

export const InstallableAppPage: React.FC<Props> = ({ installableApp, setInstallableApp, refreshInstallableApp }) => {
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const { isDraft } = useIsDraft(installableApp.channel, installableApp.id);

    const mapChannelToAppCard = useMemo(() => getMapChannelToAppCard(), []);

    const t = useMemo(() => i18n(keyset), []);

    const [operations, setOperations] = useState<Operation[]>([]);

    const settingRoute = routes.app.appPage(installableApp.channel, isDraft, 'settings', installableApp.id);
    const menuItems: IMenuMobileItem[] = [
        {
            value: `${settingRoute}/main`,
            content: t('main-information'),
            items: [
                {
                    value: `${settingRoute}/main#about`,
                    content: t('about-application'),
                },
                {
                    value: `${settingRoute}/main#application`,
                    content: t('application'),
                },
                {
                    value: `${settingRoute}/main#assets`,
                    content: t('assets'),
                },
                {
                    value: `${settingRoute}/main#agreement`,
                    content: t('agreements'),
                },
            ],
        },
    ];

    if (installableApp?.onAir) {
        menuItems.push({
            value: routes.app.installableAppSharing(installableApp.channel, installableApp.id),
            content: tAccess('header'),
        });
    }

    if (getCanViewAsAdmin()) {
        menuItems.push({
            value: routes.app.installableAppSettings(
                installableApp.channel,
                isDraft,
                'feature-flags',
                installableApp.id,
            ),
            content: t('feature-flags'),
        });
    }

    let copyright = t('yandex-copyright', { year: moment().year() });
    let externalAsideLinks: IMenuMobileItem[] = [
        {
            value: routes.documentation.aliceSkills.feedback,
            content: t('feedback'),
        },
        {
            value: routes.documentation.index,
            content: t('docs'),
        },
        {
            value: routes.documentation.termsOfUse,
            content: tFooter('terms-of-use'),
        },
    ];

    if (getIsYangoRequest()) {
        copyright = tFooter('copyrightYango', { year: moment().year() });
        externalAsideLinks = [
            {
                value: routes.documentationYango.feedback,
                content: t('feedback'),
            },
            {
                value: routes.documentationYango.index,
                content: t('docs'),
            },
        ];
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchOperations = useCallback(
        makeSafe(async () => {
            const operationsTranslated = await getOperationsTranslated(installableApp.id);
            setOperations(operationsTranslated);
        }),
        [],
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => void fetchOperations(), [installableApp.id]);

    useInterval(fetchOperations, 1 * 60 * 1000);

    return (
        <ErrorBoundary withHomepageLink>
            <SettingsPageContext.Provider
                value={{
                    isDraft,
                    isEditable: isDraft && isInDevelopment(installableApp),
                    hasUnsavedChanges,
                    setHasUnsavedChanges,
                }}
            >
                <CurrentAliceAppContext.Provider
                    value={{
                        aliceApp: installableApp,
                        refreshAliceApp: refreshInstallableApp,
                        operations,
                        addOperation: () => {},
                        lastOperationType: operations?.[0]?.type,
                        lastOperationComment: operations?.[0]?.comment,
                        fetchOperations,
                    }}
                >
                    <SettingPageLayout>
                        <section className={styles.container}>
                            <div className={styles.leftColumn}>
                                <div>
                                    <div className={styles.menuHeader}>
                                        <div className={styles.menuHeader__installableAppNameBlock}>
                                            <IconSmall imageUrl={getSquareLogoUrl(installableApp)} />
                                            <h4 className={styles.menuHeader__header}>
                                                {getAppHeader(installableApp)}
                                            </h4>
                                        </div>
                                        <div className={styles.iconStatusBlock}>
                                            {getIsAdmin() && <Shield className={styles.adminIcon} />}
                                            {getIsGlobalViewer() && <Ghost className={styles.viewerIcon} />}
                                            {mapChannelToAppCard?.[installableApp?.channel]?.icon}
                                        </div>
                                    </div>
                                    <DeploymentStatus app={installableApp} />
                                    <RevisionStatus
                                        installableApp={installableApp}
                                        setInstallableApp={setInstallableApp}
                                    />
                                    <SideMenu menuItems={menuItems} />
                                </div>
                                <div>
                                    {externalAsideLinks.map((asideLink) => (
                                        <a
                                            key={asideLink.value}
                                            href={asideLink.value}
                                            className={cls(styles.link, styles.footerElement)}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {asideLink.content}
                                        </a>
                                    ))}
                                    <div className={styles.footerElement}>
                                        <NdaLink />
                                        {copyright}
                                    </div>
                                </div>
                            </div>
                            <div className={styles.installableAppPageContent}>
                                <header className={styles.header__container}>
                                    <div className={styles.header__statusContainer}>
                                        <div className={styles.header__commonStatusContainer}>
                                            <DescribeAppInIcons app={installableApp} />
                                            <div className={styles.header__operationStatusDesktop}>
                                                <OperationStatus isDraft={isDraft} />
                                            </div>
                                        </div>
                                        <DeploymentStatusMobile app={installableApp} />
                                    </div>
                                </header>
                                <MenuMobile menuItems={menuItems} externalAsideLinks={externalAsideLinks}>
                                    <Routes>
                                        <Route
                                            path="settings/*"
                                            element={
                                                <InstallableAppSettingsPage
                                                    installableApp={installableApp}
                                                    setInstallableApp={setInstallableApp}
                                                />
                                            }
                                        />
                                        <Route path="sharing" element={<SharingPage aliceApp={installableApp} />} />
                                        {installableApp.revisions
                                            .filter((revision) => !revision.isActive)
                                            .map((revision) => (
                                                <Route
                                                    key={revision.publishedAt}
                                                    path={getRevisionSlug(revision)}
                                                    element={
                                                        <InstallableAppRevision
                                                            revision={revision}
                                                            app={installableApp}
                                                            setInstallableApp={setInstallableApp}
                                                        />
                                                    }
                                                />
                                            ))}
                                        <Route
                                            path="*"
                                            element={
                                                <Navigate
                                                    to={routes.app.appPage(
                                                        installableApp.channel,
                                                        true,
                                                        'settings',
                                                        installableApp.id,
                                                    )}
                                                    replace
                                                />
                                            }
                                        />
                                    </Routes>
                                </MenuMobile>
                                <div className={styles.footerMobile}>
                                    <NdaLink />
                                    {copyright}
                                </div>
                            </div>
                        </section>
                    </SettingPageLayout>
                </CurrentAliceAppContext.Provider>
            </SettingsPageContext.Provider>
        </ErrorBoundary>
    );
};
